import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

// ── date helpers (Brisbane, local YYYY-MM-DD math, no tz drift) ──
function todayBne(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Brisbane',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function mondayOf(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dow = dt.getUTCDay(); // 0 Sun .. 6 Sat
  const diff = dow === 0 ? -6 : 1 - dow;
  return addDays(ymd, diff);
}
function fmtDay(ymd: string): { wd: string; d: string } {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return {
    wd: dt.toLocaleDateString('en-AU', { weekday: 'short', timeZone: 'UTC' }),
    d: dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
  };
}
function fmtRange(weekStart: string): string {
  const a = fmtDay(weekStart);
  const b = fmtDay(addDays(weekStart, 6));
  return `${a.d} – ${b.d}`;
}

const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // index = dow (0 Sun..6 Sat)

const btn: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  background: 'var(--color-surface)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
};

export function Availability() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayBne()));

  const { data: contractors = [], isLoading, error } = useQuery({
    queryKey: ['availability', weekStart],
    queryFn: () => api.availabilityWeek(weekStart),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['availability'] });

  const overrideMut = useMutation({
    mutationFn: (v: { contractorId: number; day: string; available: boolean }) =>
      api.availabilitySetOverride(isAdmin ? v : { day: v.day, available: v.available }),
    onSuccess: invalidate,
  });
  const clearMut = useMutation({
    mutationFn: (v: { contractorId: number; day: string }) =>
      api.availabilityClearOverride(isAdmin ? v : { day: v.day }),
    onSuccess: invalidate,
  });
  const defaultMut = useMutation({
    mutationFn: (v: { contractorId: number; dows: number[] }) =>
      api.availabilitySetDefault(isAdmin ? v : { dows: v.dows }),
    onSuccess: invalidate,
  });
  const copyMut = useMutation({
    mutationFn: () => api.availabilityCopyWeek({ fromWeekStart: addDays(weekStart, -7), toWeekStart: weekStart }),
    onSuccess: invalidate,
  });

  // Add / remove people (admin only).
  const [newName, setNewName] = useState('');
  const addPersonMut = useMutation({
    mutationFn: (name: string) => api.createContractor({ name }),
    onSuccess: () => { setNewName(''); invalidate(); },
  });
  const removePersonMut = useMutation({
    mutationFn: (id: number) => api.deleteContractor(id),
    onSuccess: invalidate,
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const renamePersonMut = useMutation({
    mutationFn: (v: { id: number; name: string }) => api.updateContractor(v.id, { name: v.name }),
    onSuccess: () => { setEditingId(null); invalidate(); },
  });

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const today = todayBne();

  const toggleDay = (contractorId: number, day: { date: string; available: boolean }) =>
    overrideMut.mutate({ contractorId, day: day.date, available: !day.available });

  const toggleDefaultDow = (contractorId: number, currentDows: number[], dow: number) => {
    const next = currentDows.includes(dow)
      ? currentDows.filter((d) => d !== dow)
      : [...currentDows, dow].sort((a, b) => a - b);
    defaultMut.mutate({ contractorId, dows: next });
  };

  const resetWeekToPattern = (c: { contractorId: number; days: { date: string; source: string }[] }) => {
    c.days.filter((d) => d.source === 'override').forEach((d) => clearMut.mutate({ contractorId: c.contractorId, day: d.date }));
  };

  return (
    <div style={{ padding: 'var(--spacing-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        <div>
          <h1 style={{ margin: 0 }}>Availability</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--color-text-muted)' }}>
            {fmtRange(weekStart)}
            {!isAdmin && ' · mark yourself available or unavailable'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
          <button style={btn} onClick={() => setWeekStart(addDays(weekStart, -7))}>‹ Prev</button>
          <button style={btn} onClick={() => setWeekStart(mondayOf(today))}>This week</button>
          <button style={btn} onClick={() => setWeekStart(addDays(weekStart, 7))}>Next ›</button>
          {isAdmin && (
            <button
              style={{ ...btn, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)' }}
              disabled={copyMut.isPending}
              onClick={() => copyMut.mutate()}
              title="Copy last week's availability into this week"
            >
              {copyMut.isPending ? 'Copying…' : '⧉ Copy last week'}
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap', marginTop: 'var(--spacing-3)', padding: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-surface-elevated)' }}>
          <strong style={{ fontSize: 'var(--font-size-sm)' }}>Add a person</strong>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Full name"
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) addPersonMut.mutate(newName.trim()); }}
            style={{ flex: 1, minWidth: 180, minHeight: 40, padding: 'var(--spacing-2)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', boxSizing: 'border-box', fontSize: 'var(--font-size-sm)' }}
          />
          <button
            onClick={() => newName.trim() && addPersonMut.mutate(newName.trim())}
            disabled={!newName.trim() || addPersonMut.isPending}
            style={{ ...btn, background: 'var(--color-accent)', color: 'var(--color-text-inverse)', borderColor: 'var(--color-accent)', fontWeight: 600 }}
          >
            {addPersonMut.isPending ? 'Adding…' : '＋ Add'}
          </button>
          {addPersonMut.isError && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-error)' }}>{(addPersonMut.error as Error).message}</span>}
        </div>
      )}

      {isLoading && <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>}
      {error && <p style={{ color: 'var(--color-error)' }}>{(error as Error).message}</p>}
      {!isLoading && contractors.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>No contractors to show.</p>
      )}

      {contractors.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 'var(--spacing-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-elevated)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--spacing-2) var(--spacing-3)', minWidth: 220 }}>Contractor</th>
                {days.map((d) => {
                  const f = fmtDay(d);
                  return (
                    <th key={d} style={{ padding: 'var(--spacing-2)', textAlign: 'center', borderLeft: '1px solid var(--color-border)', background: d === today ? 'rgba(0,176,151,0.08)' : undefined }}>
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>{f.wd}</div>
                      <div style={{ fontWeight: 700 }}>{f.d}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {contractors.map((c) => (
                <tr key={c.contractorId} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: 'var(--spacing-3)', verticalAlign: 'top' }}>
                    {isAdmin && editingId === c.contractorId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                        <input
                          value={editName}
                          autoFocus
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter' && editName.trim()) renamePersonMut.mutate({ id: c.contractorId, name: editName.trim() }); if (e.key === 'Escape') setEditingId(null); }}
                          style={{ minWidth: 140, minHeight: 34, padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', background: 'var(--color-background)', color: 'var(--color-text)', fontSize: 'var(--font-size-sm)', fontWeight: 700 }}
                        />
                        <button onClick={() => editName.trim() && renamePersonMut.mutate({ id: c.contractorId, name: editName.trim() })} disabled={!editName.trim() || renamePersonMut.isPending}
                          style={{ background: 'var(--color-accent)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--radius-default)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: '4px 10px', fontWeight: 600 }}>Save</button>
                        <button onClick={() => setEditingId(null)}
                          style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', color: 'var(--color-text)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: '4px 10px' }}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 700 }}>{c.name}</div>
                        {isAdmin && (
                          <>
                            <button
                              onClick={() => { setEditingId(c.contractorId); setEditName(c.name); }}
                              title="Edit name"
                              style={{ flexShrink: 0, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', color: 'var(--color-text)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: '2px 8px' }}
                            >
                              ✎ Edit
                            </button>
                            <button
                              onClick={() => { if (confirm(`Remove ${c.name} from the team? Their past job history is kept, but they'll no longer appear here or in booking.`)) removePersonMut.mutate(c.contractorId); }}
                              disabled={removePersonMut.isPending}
                              title="Remove this person"
                              style={{ flexShrink: 0, background: 'none', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-default)', color: 'var(--color-error)', cursor: 'pointer', fontSize: 'var(--font-size-xs)', padding: '2px 8px' }}
                            >
                              ✕ Remove
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {c.services.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: '4px 0' }}>
                        {c.services.slice(0, 6).map((s) => (
                          <span key={s} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: 'var(--color-light-blue, #8DD2E5)', color: 'var(--color-navy, #1B365D)' }}>{s}</span>
                        ))}
                        {c.services.length > 6 && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>+{c.services.length - 6}</span>}
                      </div>
                    )}
                    <div style={{ marginTop: 4, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      Default pattern:
                      <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                        {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
                          const on = c.defaultDows.includes(dow);
                          return (
                            <button
                              key={dow}
                              onClick={() => toggleDefaultDow(c.contractorId, c.defaultDows, dow)}
                              title={on ? 'Available by default' : 'Off by default'}
                              style={{ width: 22, height: 22, borderRadius: 4, cursor: 'pointer', border: '1px solid var(--color-border)', fontSize: 11, fontWeight: 700, background: on ? 'var(--color-success, #2e7d32)' : 'var(--color-surface)', color: on ? '#fff' : 'var(--color-text-muted)' }}
                            >
                              {DOW_LABELS[dow]}
                            </button>
                          );
                        })}
                      </div>
                      <button onClick={() => resetWeekToPattern(c)} style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', padding: 0, fontSize: 'var(--font-size-xs)' }}>
                        ↺ Reset week to pattern
                      </button>
                    </div>
                  </td>
                  {c.days.map((day) => (
                    <td key={day.date} style={{ borderLeft: '1px solid var(--color-border)', textAlign: 'center', padding: 6, background: day.date === today ? 'rgba(0,176,151,0.05)' : undefined }}>
                      <button
                        onClick={() => toggleDay(c.contractorId, day)}
                        disabled={overrideMut.isPending}
                        title={day.source === 'override' ? 'Set for this date (overrides pattern)' : 'From default pattern — click to set'}
                        style={{
                          width: '100%', minHeight: 44, borderRadius: 6, cursor: 'pointer',
                          border: day.source === 'override' ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                          background: day.available ? 'var(--color-success, #2e7d32)' : 'var(--color-surface-elevated)',
                          color: day.available ? '#fff' : 'var(--color-text-muted)',
                          fontWeight: 700, fontSize: 'var(--font-size-sm)',
                        }}
                      >
                        {day.available ? '✓' : '—'}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        ✓ available · — unavailable · a coloured border means that day was set specifically (overriding the weekly pattern).
      </p>
    </div>
  );
}
